import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { Button, Form, Icon, Message, Modal, Popup } from "semantic-ui-react";
import { createPrimumMeeting } from "../../api/primum";
import usePrimumSubscription, {
  refreshPrimumSubscription,
} from "../../hooks/usePrimumSubscription";
import CallPopup from "../activeCall/CallPopup";

const SUBSCRIBE_WAIT_MS = 15 * 60 * 1000;

const MeetNowContext = createContext({
  openMeetNow: () => {},
  awaitSubscribe: () => {},
});

/**
 * Holds the Meet Now modal above the call UI, so hanging up or minimising
 * the call bar doesn't throw away what the user has typed.
 */
export function MeetNowProvider({ children }) {
  const queryClient = useQueryClient();
  const [request, setRequest] = useState(null);
  const [awaitingSubscribe, setAwaitingSubscribe] = useState(false);
  const nextId = useRef(0);

  // re-check when they come back from Primum's subscribe page. Held here, not
  // in the button, so it survives the in-call button going away. Gives up
  // after a while so an abandoned sign-up doesn't refresh on every focus
  useEffect(() => {
    if (!awaitingSubscribe) return;

    let checking = false;
    const onFocus = () => {
      if (checking) return;
      checking = true;
      refreshPrimumSubscription(queryClient)
        .then((subscription) => {
          if (subscription?.active) setAwaitingSubscribe(false);
        })
        .catch(() => {})
        .finally(() => (checking = false));
    };
    const giveUp = setTimeout(
      () => setAwaitingSubscribe(false),
      SUBSCRIBE_WAIT_MS,
    );

    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearTimeout(giveUp);
    };
  }, [awaitingSubscribe, queryClient]);

  const value = useMemo(
    () => ({
      openMeetNow: (defaults) =>
        setRequest({ ...defaults, id: ++nextId.current }),
      awaitSubscribe: () => setAwaitingSubscribe(true),
    }),
    [],
  );

  // only close the modal this request opened, not one opened since
  const close = (id) => setRequest((r) => (r?.id === id ? null : r));

  return (
    <MeetNowContext.Provider value={value}>
      {children}
      {request && (
        <MeetNowModal
          key={request.id}
          defaultPhone={request.phone}
          defaultName={request.patientName}
          onClose={() => close(request.id)}
        />
      )}
    </MeetNowContext.Provider>
  );
}

/**
 * Primum VideoMeet "Meet Now". Shows Subscribe instead when the customer's
 * org has no VideoMeet subscription.
 *
 * variant "call" is an icon button for the in-call action bar, "page" a full
 * width button for the Calls screen.
 */
function MeetNowButton({ variant = "page", phone = "", patientName = "" }) {
  const { enabled, data, isLoading, isError, error, refetch } =
    usePrimumSubscription();
  const { openMeetNow, awaitSubscribe } = useContext(MeetNowContext);

  if (!enabled) return null;

  const inCall = variant === "call";

  if (isLoading || isError) {
    if (inCall) return null;

    return (
      <Popup
        disabled={!isError}
        content={(
          error?.response?.data?.message || "Could not check VideoMeet"
        ).replace(/\.?$/, ". Click to try again.")}
        trigger={
          <span>
            <Button
              fluid
              basic
              color="blue"
              style={style.pageButton}
              loading={isLoading}
              disabled={isLoading}
              onClick={() => refetch()}
            >
              <Icon name="video camera" />
              Meet Now
            </Button>
          </span>
        }
      />
    );
  }

  if (!data?.active) {
    const subscribe = () => {
      window.open(data?.subscribe_url, "_blank", "noopener");
      awaitSubscribe();
    };

    return inCall ? (
      <CallPopup
        position="top center"
        content="Subscribe to VideoMeet to send video meeting links"
        trigger={
          <Button color="black" onClick={subscribe} icon>
            <Icon name="video camera" />
          </Button>
        }
      />
    ) : (
      <Button
        fluid
        basic
        color="blue"
        style={style.pageButton}
        onClick={subscribe}
      >
        <Icon name="video camera" />
        Subscribe to VideoMeet
      </Button>
    );
  }

  const open = () => openMeetNow({ phone, patientName });

  return inCall ? (
    <CallPopup
      position="top center"
      content="Meet Now: send the caller a video meeting link"
      trigger={
        <Button color="black" onClick={open} icon>
          <Icon name="video camera" />
        </Button>
      }
    />
  ) : (
    <Button fluid basic color="blue" style={style.pageButton} onClick={open}>
      <Icon name="video camera" />
      Meet Now
    </Button>
  );
}

// defaults are only read when the modal opens, so a call update can't wipe
// what's been typed
function MeetNowModal({ onClose, defaultPhone, defaultName }) {
  const [form, setForm] = useState({
    patient_name: defaultName ?? "",
    phone: defaultPhone ?? "",
    email: "",
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  // functional updates, so autofill setting several fields at once keeps them all
  const update = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((errs) => ({ ...errs, [field]: undefined }));
    setError("");
  };

  const fieldError = (field) =>
    errors[field] ? { content: errors[field][0], pointing: "above" } : null;

  const canSend = !sending && (form.phone || form.email);

  const handleSubmit = async () => {
    // Enter and the button can both submit
    if (!canSend) return;
    setSending(true);

    // opened now, while we still have the click, or the browser blocks it.
    // No "noopener" here: that makes window.open return null
    const tab = window.open("", "_blank");

    let res;
    try {
      res = await createPrimumMeeting({
        patient_name: form.patient_name || null,
        phone: form.phone || null,
        email: form.email || null,
      });
    } catch (e) {
      tab?.close();
      if (e?.response?.status === 422) {
        setErrors(e.response.data.errors ?? {});
      } else {
        setError(
          e?.response?.data?.message || "Could not create the video meeting",
        );
      }
      setSending(false);
      return;
    }

    const hostUrl = res.data.host_url;
    // the user may have closed the blank tab while we waited
    const tabOpen = tab && !tab.closed;

    if (tabOpen && hostUrl) {
      tab.opener = null;
      tab.location.href = hostUrl;
    } else {
      tab?.close();
    }

    toast(
      <MeetingSentToast
        inviteUrl={res.data.invite_url}
        // only when we couldn't use the tab; the link works once
        hostUrl={tabOpen ? null : hostUrl}
      />,
      { type: "success", autoClose: false },
    );
    onClose();
  };

  return (
    // above the in-call screen (ActiveCallContainer is z-index 99999)
    <Modal
      open
      // closing mid-send would lose the result, so wait for it
      onClose={() => !sending && onClose()}
      closeOnDimmerClick={false}
      size="tiny"
      dimmer={{ style: { zIndex: 100000 } }}
    >
      <Modal.Header>Meet Now</Modal.Header>
      <Modal.Content>
        <p>
          VideoMeet will send the patient a link to join by text message, email,
          or both. Enter a mobile number or an email address.
        </p>
        <Form
          onKeyDown={(e) =>
            // not while an IME is composing, where Enter accepts the text
            e.key === "Enter" && !e.nativeEvent.isComposing && handleSubmit()
          }
        >
          <Form.Input
            label="Patient name (optional)"
            value={form.patient_name}
            onChange={update("patient_name")}
            error={fieldError("patient_name")}
          />
          {/* the caller's number may be a landline, which can't get a text */}
          <Form.Input
            label="Mobile number"
            value={form.phone}
            onChange={update("phone")}
            error={fieldError("phone")}
          />
          <Form.Input
            label="Email"
            type="email"
            value={form.email}
            onChange={update("email")}
            error={fieldError("email")}
          />
        </Form>

        {error && (
          <Message negative>
            <p>{error}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          positive
          icon="video camera"
          labelPosition="right"
          content="Send link"
          loading={sending}
          disabled={!canSend}
          onClick={handleSubmit}
        />
      </Modal.Actions>
    </Modal>
  );
}

// Primum only confirm the email was queued, so don't claim the SMS arrived
function MeetingSentToast({ inviteUrl, hostUrl }) {
  const [copied, setCopied] = useState(false);

  return (
    <div>
      <p>Meeting link sent</p>
      {hostUrl && (
        <Button
          size="mini"
          icon="video camera"
          content="Join meeting"
          as="a"
          href={hostUrl}
          target="_blank"
          rel="noopener noreferrer"
          // keep the login ticket out of Sentry replays
          data-sentry-block
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {inviteUrl && (
        <Button
          size="mini"
          icon={copied ? "check" : "copy"}
          content={copied ? "Copied" : "Copy link"}
          onClick={(e) => {
            // don't let the click close the toast
            e.stopPropagation();
            navigator.clipboard
              .writeText(inviteUrl)
              .then(() => setCopied(true))
              .catch(() => toast("Could not copy the link", { type: "error" }));
          }}
        />
      )}
    </div>
  );
}

// prefill from the other party, but only for outside numbers, not extensions
export function meetNowCaller(activeCall, activeCallParties) {
  const number = activeCall?.callNumber;

  if (!number || number.length <= 6) return {};

  // phone-control parties have no callNumber, so fall back to the only party
  const party =
    activeCallParties?.find((p) => p.callNumber === number) ??
    (activeCallParties?.length === 1 ? activeCallParties[0] : null);

  return {
    phone: number,
    patientName: party?.name && party.name !== number ? party.name : "",
  };
}

const style = {
  pageButton: {
    marginBottom: "12px",
  },
};

export default MeetNowButton;
